<template>
  <div>
    <plant-list-add-new 
      :is-add-new-plant-sidebar-active.sync="isAddNewPlantSidebarActive"
      @refetch-data="refetchData" 
    />

    <b-card-code title="">
      <!-- input search -->
      <div class="custom-search d-flex justify-content-end">
        <b-form-group>
          <div class="d-flex align-items-center justify-content-end">
            <label class="mr-1">{{ $t('Search') }}</label>
            <b-form-input 
              v-model="searchTerm" 
              :placeholder="$t('Search')" 
              type="text" 
              class="d-inline-block mr-1" 
            />
            <b-button 
              variant="primary" 
              @click="isAddNewPlantSidebarActive = true"
            >
              <span class="text-nowrap">{{ $t('plant.addNew') }}</span>
            </b-button>
          </div>
        </b-form-group>
      </div>

      <!-- table -->
      <vue-good-table 
        :columns="columns" 
        :rows="rows" 
        :line-numbers="true" 
        :search-options="{
          enabled: true,
          externalQuery: searchTerm
        }" 
        :pagination-options="{
          enabled: true,
          perPage: pageLength
        }"
      >
        <template slot="table-row" slot-scope="props">
          <!-- Column: Action -->
          <span v-if="props.column.field === 'action'">
            <span>
              <b-dropdown 
                variant="link" 
                toggle-class="text-decoration-none" 
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon 
                    icon="MoreVerticalIcon" 
                    size="16" 
                    class="text-body align-middle mr-25" 
                  />
                </template>
                <b-dropdown-item 
                  :to="{ name: 'plant-edit', params: { id: props.row.id } }"
                >
                  <feather-icon icon="Edit2Icon" class="mr-50" />
                  <span>{{ $t('Edit') }}</span>
                </b-dropdown-item>
                <b-dropdown-item @click="deleteData(props.row.id)">
                  <feather-icon icon="TrashIcon" class="mr-50" />
                  <span>{{ $t('Delete') }}</span>
                </b-dropdown-item>
              </b-dropdown>
            </span>
          </span>

          <!-- Column: Common -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap">
                Showing 1 to
              </span>
              <b-form-select 
                v-model="pageLength" 
                :options="['10', '25', '50']" 
                class="mx-1"
                @input="(value) => props.perPageChanged({ currentPerPage: value })" 
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination 
                :value="1" 
                :total-rows="props.total" 
                :per-page="pageLength" 
                first-number 
                last-number
                align="right" 
                prev-class="prev-item" 
                next-class="next-item" 
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </b-card-code>
  </div>
</template>

<script>
import {
  VBModal, BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination, BFormGroup, BSidebar, BFormSelect
} from 'bootstrap-vue'
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import { VueGoodTable } from 'vue-good-table'
import PlantListAddNew from './PlantListAddNew.vue'
import store from '@/store'
import plantStoreModule from './plantStoreModule'
import { onMounted, onUnmounted, ref } from '@vue/composition-api'
import { getUserData } from '@/auth/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import { useToast } from 'vue-toastification/composition'

export default {
  name: 'plant-list',
  components: {
    BCardCode,
    VBModal, BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
    BBadge, BDropdown, BDropdownItem, BPagination, BFormGroup, BSidebar, BFormSelect,
    VueGoodTable,
    PlantListAddNew
  },
  data() {
    return {
      pageLength: 10,
      dir: false,
      columns: [
        {
          label: this.$t('plant.name'),
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'Search Name',
          },
        },
        {
          label: this.$t('plant.address'),
          field: 'address',
          filterOptions: {
            enabled: true,
            placeholder: 'Search Address',
          },
        },
        {
          label: this.$t('plant.longitude'),
          field: 'longitude',
          filterOptions: {
            enabled: true,
            placeholder: 'Search Longitude',
          },
        },
        {
          label: this.$t('plant.latitude'),
          field: 'latitude',
          filterOptions: {
            enabled: true,
            placeholder: 'Search Latitude',
          },
        },
        {
          label: this.$t('Action'),
          field: 'action',
        },
      ],
      searchTerm: '',
    }
  },

  setup() {
    const PLANT_APP_STORE_MODULE = 'hr-plant'
    const toast = useToast()
    const rows = ref([])
    
    // Register module
    if (!store.hasModule(PLANT_APP_STORE_MODULE)) {
      store.registerModule(PLANT_APP_STORE_MODULE, plantStoreModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(PLANT_APP_STORE_MODULE)) {
        store.unregisterModule(PLANT_APP_STORE_MODULE)
      }
    })

    onMounted(() => {
      refetchData()
    })

    const isAddNewPlantSidebarActive = ref(false)

    const refetchData = function () {
      const userData = getUserData()
      store.dispatch('fetchPlants', { clientGuid: userData.setting.clientguid })
        .then(response => {
          rows.value = response.data
        })
        .catch(reject => {
          rows.value = []
        })
    }

    const deleteData = function (id) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          store.dispatch('deletePlant', { id })
            .then(response => {
              this.$swal({
                icon: 'success',
                title: 'Deleted!',
                timer: 2000,
                text: 'Plant has been deleted.',
              })
              refetchData()
            })
            .catch(error => {
              this.$swal({
                icon: 'error',
                title: 'Failed!',
                text: 'Plant could not be deleted.',
                customClass: {
                  confirmButton: 'btn btn-danger',
                },
              })
            })
        }
      })
    }

    return {
      isAddNewPlantSidebarActive,
      refetchData,
      deleteData,
      rows
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.flatpickr-calendar {
  overflow: hidden;
  display: none;
}
</style>

